import React, { useEffect, useMemo, useState } from "react";
import ModalShell from "./ModalShell";
import {
  parseOptionalFiniteNumber,
  parseRequiredFiniteNumber,
} from "../shared/utils/csvNumber";
import { parseSopMoneyField } from "../shared/utils/sopFinance";
import {
  OPS_PLAN_LABEL_PLAN_HRS,
  OPS_PLAN_SOP_ACTUAL_ENTERED_HRS_PLACEHOLDER,
  OPS_PLAN_SOP_REQUIRED_ALERT,
  OPS_PLAN_SOP_COL_ENTERED_HRS,
  OPS_PLAN_SOP_COL_REVENUE,
  OPS_PLAN_SOP_COL_EXPENSES,
} from "../shared/utils/opsPlanLabels";

function parseActualEnteredHrs(raw) {
  if (raw === undefined || raw === null || String(raw).trim() === "") {
    return { ok: true, value: 0 };
  }
  return parseOptionalFiniteNumber(raw, {
    min: 0,
    defaultIfEmpty: 0,
    fieldName: "actual_entered_hrs",
  });
}

/**
 * Create or edit a Strategic Operating Plan row (ModalShell).
 * Parent owns API via onSubmit({ fiscal_year, program, planned_hrs, actual_entered_hrs, revenue, expenses, _id? }).
 */
export default function SopEditorModal({
  open,
  mode = "create",
  row = null,
  programOptions = [],
  defaultFiscalYear = "",
  onCancel,
  onSubmit,
}) {
  const [fiscalYear, setFiscalYear] = useState("");
  const [program, setProgram] = useState("");
  const [plannedHrs, setPlannedHrs] = useState("");
  const [actualEnteredHrs, setActualEnteredHrs] = useState("");
  const [revenue, setRevenue] = useState("");
  const [expenses, setExpenses] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setError("");
    if (isEdit && row) {
      setFiscalYear(row.fiscal_year || "");
      setProgram(row.program || "");
      setPlannedHrs(String(row.planned_hrs ?? ""));
      setActualEnteredHrs(
        row.actual_entered_hrs != null && row.actual_entered_hrs !== ""
          ? String(row.actual_entered_hrs)
          : ""
      );
      setRevenue(
        row.revenue != null && row.revenue !== "" ? String(row.revenue) : ""
      );
      setExpenses(
        row.expenses != null && row.expenses !== "" ? String(row.expenses) : ""
      );
    } else {
      setFiscalYear(defaultFiscalYear || "");
      setProgram("");
      setPlannedHrs("");
      setActualEnteredHrs("");
      setRevenue("");
      setExpenses("");
    }
  }, [open, mode, row, isEdit, defaultFiscalYear]);

  const programSelectOptions = useMemo(() => {
    const names = (programOptions || []).map((n) => String(n || "").trim()).filter(Boolean);
    const set = new Set(names);
    const current = (program || "").trim();
    if (current && !set.has(current)) names.push(current);
    return names;
  }, [programOptions, program]);

  async function handleSubmit(e) {
    e.preventDefault();
    const fy = (fiscalYear || "").trim();
    const prog = (program || "").trim();
    const plannedRes = parseRequiredFiniteNumber(plannedHrs, {
      min: 0,
      fieldName: "planned_hrs",
    });
    const enteredRes = parseActualEnteredHrs(actualEnteredHrs);
    const revenueRes = parseSopMoneyField(revenue, "revenue");
    const expensesRes = parseSopMoneyField(expenses, "expenses");

    if (!fy || !prog) {
      setError(OPS_PLAN_SOP_REQUIRED_ALERT);
      return;
    }
    if (!plannedRes.ok) {
      setError(plannedRes.error || OPS_PLAN_SOP_REQUIRED_ALERT);
      return;
    }
    if (!enteredRes.ok) {
      setError(enteredRes.error);
      return;
    }
    if (!revenueRes.ok) {
      setError(revenueRes.error);
      return;
    }
    if (!expensesRes.ok) {
      setError(expensesRes.error);
      return;
    }

    const payload = {
      fiscal_year: fy,
      program: prog,
      planned_hrs: plannedRes.value,
      actual_entered_hrs: enteredRes.value,
      revenue: revenueRes.value,
      expenses: expensesRes.value,
    };
    if (isEdit && row?._id) payload._id = row._id;

    setSubmitting(true);
    setError("");
    try {
      await onSubmit(payload);
    } catch (err) {
      const msg = err && typeof err.message === "string" ? err.message.trim() : "";
      setError(msg || "Failed to save Strategic Operating Plan.");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  }

  const title = isEdit ? "Edit Strategic Operating Plan" : "New Strategic Operating Plan";
  const submitLabel = isEdit ? "Save" : "Create";

  return (
    <ModalShell
      open={open}
      onClose={submitting ? undefined : onCancel}
      title={title}
      maxWidth={520}
      zIndex={1090}
      closeOnOverlayClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={submitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="sop-editor-modal-form"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </>
      }
    >
      <form id="sop-editor-modal-form" className="modal-form" onSubmit={handleSubmit}>
        {error ? (
          <div style={{ color: "#c92a2a", fontSize: 13, marginBottom: 12 }}>{error}</div>
        ) : null}

        <div className="form-field">
          <label htmlFor="sop-editor-fiscal-year">Fiscal Year *</label>
          <input
            id="sop-editor-fiscal-year"
            type="text"
            className="input"
            value={fiscalYear}
            onChange={(e) => setFiscalYear(e.target.value)}
            disabled={submitting}
            autoFocus={!isEdit}
            placeholder="e.g. FY2026"
          />
        </div>

        <div className="form-field">
          <label htmlFor="sop-editor-program">Program *</label>
          <select
            id="sop-editor-program"
            className="input"
            value={program}
            onChange={(e) => setProgram(e.target.value)}
            disabled={submitting}
            title="Program"
          >
            <option value="">— Select program —</option>
            {programSelectOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="sop-editor-planned-hrs">{OPS_PLAN_LABEL_PLAN_HRS} *</label>
          <input
            id="sop-editor-planned-hrs"
            type="text"
            className="input input-number-no-spin"
            value={plannedHrs}
            onChange={(e) => setPlannedHrs(e.target.value)}
            disabled={submitting}
            placeholder={OPS_PLAN_LABEL_PLAN_HRS}
          />
        </div>

        <div className="form-field">
          <label htmlFor="sop-editor-actual-entered-hrs">{OPS_PLAN_SOP_COL_ENTERED_HRS}</label>
          <input
            id="sop-editor-actual-entered-hrs"
            type="text"
            className="input input-number-no-spin"
            value={actualEnteredHrs}
            onChange={(e) => setActualEnteredHrs(e.target.value)}
            disabled={submitting}
            placeholder={OPS_PLAN_SOP_ACTUAL_ENTERED_HRS_PLACEHOLDER}
          />
        </div>

        <div className="form-field">
          <label htmlFor="sop-editor-revenue">{OPS_PLAN_SOP_COL_REVENUE}</label>
          <input
            id="sop-editor-revenue"
            type="text"
            className="input input-number-no-spin"
            value={revenue}
            onChange={(e) => setRevenue(e.target.value)}
            disabled={submitting}
            placeholder={OPS_PLAN_SOP_COL_REVENUE}
          />
        </div>

        <div className="form-field">
          <label htmlFor="sop-editor-expenses">{OPS_PLAN_SOP_COL_EXPENSES}</label>
          <input
            id="sop-editor-expenses"
            type="text"
            className="input input-number-no-spin"
            value={expenses}
            onChange={(e) => setExpenses(e.target.value)}
            disabled={submitting}
            placeholder={OPS_PLAN_SOP_COL_EXPENSES}
          />
        </div>
      </form>
    </ModalShell>
  );
}
